import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  Upload, Download, FileText, Check, X, AlertTriangle, 
  RefreshCw, ArrowRight, History, ChevronDown, ChevronUp,
  Database, Users, Calendar, Clock, Banknote, Settings,
  HelpCircle, Play, RotateCcw, Eye, FileSpreadsheet,
  Sparkles, Shield, Key, Server, FileDown, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const MIGRATION_TYPES = [
  { id: 'employees', name: 'Employees', icon: Users, description: 'Employee master data' },
  { id: 'payroll', name: 'Payroll History', icon: Banknote, description: 'Historical payroll records' },
  { id: 'leave', name: 'Leave Balances', icon: Calendar, description: 'Leave entitlements' },
  { id: 'attendance', name: 'Attendance', icon: Clock, description: 'Attendance records' },
];

const COMPETITOR_SYSTEMS = [
  { id: 'kakitangan', name: 'Kakitangan', color: 'bg-blue-500' },
  { id: 'payrollpanda', name: 'PayrollPanda', color: 'bg-orange-500' },
  { id: 'swingvy', name: 'Swingvy', color: 'bg-purple-500' },
  { id: 'briohrhub', name: 'BrioHR', color: 'bg-teal-500' },
  { id: 'hr2000', name: 'HR2000', color: 'bg-red-500' },
  { id: 'hr2easy', name: 'HR2easy', color: 'bg-green-500' },
];

const COMPARISON_FEATURES = [
  { feature: 'Data Safety', myhr: 'Your data, your servers', others: 'Vendor-controlled' },
  { feature: 'Ownership', myhr: 'Single-tenant, full ownership', others: 'Multi-tenant SaaS' },
  { feature: 'Source Code', myhr: 'Perpetual license included', others: 'No access' },
  { feature: 'Vendor Support', myhr: 'Optional, controlled by you', others: 'Mandatory subscription' },
  { feature: 'Exit Strategy', myhr: 'Full data + code portability', others: 'Vendor lock-in' },
];

export default function DataMigration() {
  const [activeTab, setActiveTab] = useState('import');
  const [templates, setTemplates] = useState([]);
  const [sourceProfiles, setSourceProfiles] = useState([]);
  const [migrations, setMigrations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Import wizard state
  const [wizardStep, setWizardStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSource, setSelectedSource] = useState('generic');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [duplicateStrategy, setDuplicateStrategy] = useState('skip');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Auto-detection state
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [autoDetectResult, setAutoDetectResult] = useState(null);
  
  // Dialogs
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedMigration, setSelectedMigration] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, profilesRes, historyRes] = await Promise.all([
        axios.get(`${API}/migration/templates`),
        axios.get(`${API}/migration/source-profiles`),
        axios.get(`${API}/migration/history`)
      ]);
      setTemplates(templatesRes.data);
      setSourceProfiles(profilesRes.data);
      setMigrations(historyRes.data.migrations || []);
    } catch (error) {
      console.error('Failed to fetch migration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async (type) => {
    try {
      const response = await axios.get(`${API}/migration/templates/${type}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `myhr_orbit_${type}_template.csv`;
      link.click();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Auto-detect source system from CSV
  const handleAutoDetect = async (headers, sampleRows) => {
    setAutoDetecting(true);
    try {
      const response = await axios.post(`${API}/migration/auto-detect`, {
        headers,
        sample_rows: sampleRows
      });
      setAutoDetectResult(response.data);
      
      // Auto-select detected source if confidence is high
      if (response.data.confidence >= 70) {
        setSelectedSource(response.data.detected_source);
        toast.success(`Detected ${response.data.detected_source} format (${response.data.confidence}% confidence)`);
      } else {
        toast.info(`Low confidence detection. Please verify source system.`);
      }
    } catch (error) {
      console.error('Auto-detect failed:', error);
    } finally {
      setAutoDetecting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Only CSV files are supported');
      return;
    }
    
    setUploadedFile(file);
    
    // Read file for auto-detection first
    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target.result;
      const lines = content.split('\n').filter(l => l.trim());
      if (lines.length > 1) {
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        const sampleRows = lines.slice(1, 6).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const row = {};
          headers.forEach((h, i) => row[h] = values[i] || '');
          return row;
        });
        // Run auto-detection
        await handleAutoDetect(headers, sampleRows);
      }
    };
    reader.readAsText(file);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('migration_type', selectedType);
    formData.append('source_system', selectedSource);
    
    try {
      const response = await axios.post(`${API}/migration/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(response.data);
      setFieldMappings(response.data.detected_mappings || []);
      setWizardStep(2);
      toast.success(`File uploaded: ${response.data.row_count} rows detected`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const handleValidate = async () => {
    if (!uploadResult?.migration_id) return;
    
    setImporting(true);
    try {
      // Update mappings first
      await axios.put(`${API}/migration/${uploadResult.migration_id}/mapping`, {
        mappings: fieldMappings
      });
      
      // Run validation
      const response = await axios.post(`${API}/migration/${uploadResult.migration_id}/validate`);
      setValidationResult(response.data);
      setWizardStep(3);
      
      if (response.data.is_valid) {
        toast.success('Validation passed! Ready to import.');
      } else {
        toast.warning(`Validation found ${response.data.error_count} errors`);
      }
    } catch (error) {
      toast.error('Validation failed');
    } finally {
      setImporting(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!uploadResult?.migration_id) return;
    
    setImporting(true);
    try {
      const response = await axios.post(`${API}/migration/${uploadResult.migration_id}/execute`, {
        duplicate_strategy: duplicateStrategy
      });
      setImportResult(response.data);
      setWizardStep(4);
      
      if (response.data.success) {
        toast.success(`Import completed! ${response.data.stats.success} records imported.`);
        fetchData();
      } else {
        toast.error('Import failed. Check the report for details.');
      }
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleRollback = async (migrationId) => {
    if (!confirm('Are you sure you want to rollback this migration? All imported data will be deleted.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/migration/${migrationId}/rollback`);
      toast.success('Migration rolled back successfully');
      fetchData();
    } catch (error) {
      toast.error('Rollback failed');
    }
  };

  const downloadReport = async (migrationId) => {
    try {
      const response = await axios.get(`${API}/migration/${migrationId}/download-report`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `migration_report_${migrationId.slice(0, 8)}.csv`;
      link.click();
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const resetWizard = () => {
    setWizardStep(0);
    setSelectedType(null);
    setSelectedSource('generic');
    setUploadedFile(null);
    setUploadResult(null);
    setFieldMappings([]);
    setValidationResult(null);
    setImportResult(null);
    setAutoDetectResult(null);
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 80) return { label: 'High', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' };
    if (confidence >= 50) return { label: 'Medium', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
    return { label: 'Low', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'failed': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'rolled_back': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'importing': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <Database className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Data Migration</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Import data from other HR systems
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowHistoryDialog(true)}
          data-testid="migration-history-btn"
        >
          <History className="h-4 w-4 mr-2" />
          Migration History
        </Button>
      </div>

      {/* Switch from Competitor Card - Marketing Layer */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-xl p-6 text-white">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <Zap className="h-5 w-5 text-emerald-400" />
              <h2 className="text-xl font-bold">Switch in Under 24 Hours</h2>
            </div>
            <p className="text-slate-300 text-sm mb-4">
              Moving from another HR system? We make migration seamless. Download our templates, 
              upload your data, and our AI assistant will auto-detect your source format.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {COMPETITOR_SYSTEMS.map((sys) => (
                <span
                  key={sys.id}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${sys.color} text-white`}
                >
                  {sys.name}
                </span>
              ))}
            </div>
            <Button 
              variant="outline" 
              className="bg-transparent border-emerald-500 text-emerald-400 hover:bg-emerald-500/10"
              onClick={() => window.open('/docs/migration-guide.pdf', '_blank')}
            >
              <FileDown className="h-4 w-4 mr-2" />
              Download Migration Guide
            </Button>
          </div>
          
          {/* Comparison Table */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-emerald-400 mb-3">Why MyHR Orbit?</h3>
            <div className="space-y-2">
              {COMPARISON_FEATURES.map((item, idx) => (
                <div key={idx} className="flex items-center text-xs">
                  <span className="w-24 text-slate-400">{item.feature}</span>
                  <div className="flex-1 flex items-center space-x-2">
                    <span className="flex items-center text-emerald-400">
                      <Check className="h-3 w-3 mr-1" />
                      {item.myhr}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-700 flex items-center space-x-4 text-xs">
              <div className="flex items-center text-slate-400">
                <Shield className="h-3 w-3 mr-1" />
                SOC 2 Ready
              </div>
              <div className="flex items-center text-slate-400">
                <Key className="h-3 w-3 mr-1" />
                Your Keys, Your Data
              </div>
              <div className="flex items-center text-slate-400">
                <Server className="h-3 w-3 mr-1" />
                Single Tenant
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setActiveTab('import')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'import'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          Import Data
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'templates'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          Download Templates
        </button>
      </div>

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="grid md:grid-cols-2 gap-4">
          {templates.map((template) => {
            const TypeIcon = MIGRATION_TYPES.find(t => t.id === template.type)?.icon || FileText;
            return (
              <div
                key={template.type}
                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <TypeIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white">{template.display_name}</h3>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">{template.description}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadTemplate(template.type)}
                    data-testid={`download-template-${template.type}`}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  <span className="font-medium">{template.fields.filter(f => f.required).length}</span> required fields,{' '}
                  <span className="font-medium">{template.fields.length}</span> total fields
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {['Select Type', 'Upload File', 'Map Fields', 'Review', 'Complete'].map((step, idx) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  idx < wizardStep 
                    ? 'bg-emerald-500 text-white' 
                    : idx === wizardStep 
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' 
                      : 'bg-slate-100 text-slate-400 dark:bg-slate-700'
                }`}>
                  {idx < wizardStep ? <Check className="h-4 w-4" /> : idx + 1}
                </div>
                <span className={`ml-2 text-sm hidden md:block ${
                  idx <= wizardStep ? 'text-slate-900 dark:text-white' : 'text-slate-400'
                }`}>
                  {step}
                </span>
                {idx < 4 && (
                  <div className={`w-12 h-0.5 mx-2 ${
                    idx < wizardStep ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 0: Select Type */}
          {wizardStep === 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                What data do you want to import?
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {MIGRATION_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => {
                        setSelectedType(type.id);
                        setWizardStep(1);
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all hover:border-emerald-500 ${
                        selectedType === type.id
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                          : 'border-slate-200 dark:border-slate-600'
                      }`}
                      data-testid={`select-type-${type.id}`}
                    >
                      <Icon className="h-6 w-6 text-emerald-600 mb-2" />
                      <h4 className="font-semibold text-slate-900 dark:text-white">{type.name}</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">{type.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 1: Upload File */}
          {wizardStep === 1 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                Select source and upload your CSV file
              </h3>
              
              {/* Source System Selection */}
              <div className="mb-6">
                <Label className="mb-2 block">Source System</Label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedSource('generic')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedSource === 'generic'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200'
                    }`}
                  >
                    Generic CSV
                  </button>
                  {COMPETITOR_SYSTEMS.map((sys) => (
                    <button
                      key={sys.id}
                      onClick={() => setSelectedSource(sys.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedSource === sys.id
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200'
                      }`}
                    >
                      {sys.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* File Upload */}
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 text-center">
                <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Drag and drop your CSV file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                  data-testid="csv-file-input"
                />
                <label htmlFor="csv-upload">
                  <Button variant="outline" as="span" className="cursor-pointer">
                    Select CSV File
                  </Button>
                </label>
                {uploadedFile && (
                  <p className="mt-4 text-emerald-600 dark:text-emerald-400">
                    Selected: {uploadedFile.name}
                  </p>
                )}
              </div>
              
              <div className="mt-4 flex justify-between">
                <Button variant="outline" onClick={() => setWizardStep(0)}>
                  Back
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Map Fields */}
          {wizardStep === 2 && uploadResult && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                Review column mappings
              </h3>
              
              {/* Auto-Detection Result */}
              {autoDetectResult && (
                <div className={`mb-4 p-4 rounded-lg border ${
                  autoDetectResult.confidence >= 70
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700'
                    : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Sparkles className={`h-4 w-4 ${autoDetectResult.confidence >= 70 ? 'text-emerald-600' : 'text-amber-600'}`} />
                      <span className="font-medium text-slate-900 dark:text-white text-sm">
                        AI Detected: <span className="capitalize">{autoDetectResult.detected_source}</span>
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceBadge(autoDetectResult.confidence).color}`}>
                      {autoDetectResult.confidence}% {getConfidenceBadge(autoDetectResult.confidence).label}
                    </span>
                  </div>
                  {autoDetectResult.reasoning?.length > 0 && (
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      {autoDetectResult.reasoning.join(' • ')}
                    </p>
                  )}
                  
                  {/* Suggestions */}
                  {autoDetectResult.suggestions?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-600">
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Format Suggestions:</p>
                      <div className="space-y-1">
                        {autoDetectResult.suggestions.slice(0, 3).map((s, idx) => (
                          <div key={idx} className="flex items-center text-xs text-slate-600 dark:text-slate-400">
                            <AlertTriangle className="h-3 w-3 mr-2 text-amber-500" />
                            <span className="font-medium">{s.field}:</span>
                            <span className="ml-1">{s.suggestion}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Warnings */}
                  {autoDetectResult.warnings?.length > 0 && (
                    <div className="mt-2">
                      {autoDetectResult.warnings.map((w, idx) => (
                        <p key={idx} className="text-xs text-amber-600 dark:text-amber-400 flex items-center">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {w}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  <span className="font-medium">{uploadResult.row_count}</span> rows detected. 
                  We've auto-detected the column mappings below. Adjust if needed.
                </p>
              </div>
              
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {fieldMappings.map((mapping, idx) => (
                  <div key={idx} className="flex items-center space-x-4 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">Source Column</Label>
                      <p className="font-medium text-slate-900 dark:text-white">{mapping.source_column}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">Target Field</Label>
                      <p className="font-medium text-emerald-600 dark:text-emerald-400">{mapping.target_field}</p>
                    </div>
                    {mapping.required && (
                      <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">Required</span>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setWizardStep(1)}>
                  Back
                </Button>
                <Button onClick={handleValidate} disabled={importing} data-testid="validate-btn">
                  {importing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                  Validate Data
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Review & Execute */}
          {wizardStep === 3 && validationResult && (
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                Validation Results
              </h3>
              
              {/* Validation Summary */}
              <div className={`p-4 rounded-lg mb-6 ${
                validationResult.is_valid 
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700' 
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'
              }`}>
                <div className="flex items-center space-x-2">
                  {validationResult.is_valid ? (
                    <Check className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  )}
                  <span className={`font-semibold ${
                    validationResult.is_valid ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'
                  }`}>
                    {validationResult.is_valid ? 'Validation Passed' : 'Validation Failed'}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Total Rows</span>
                    <p className="font-semibold text-slate-900 dark:text-white">{validationResult.total_rows}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Valid</span>
                    <p className="font-semibold text-emerald-600">{validationResult.valid_rows}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Errors</span>
                    <p className="font-semibold text-red-600">{validationResult.error_count}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Duplicates</span>
                    <p className="font-semibold text-amber-600">{validationResult.duplicate_count}</p>
                  </div>
                </div>
              </div>
              
              {/* Errors */}
              {validationResult.errors?.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">Errors (first 10)</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {validationResult.errors.slice(0, 10).map((err, idx) => (
                      <div key={idx} className="text-sm text-red-600 dark:text-red-400">
                        Row {err.row_number}: {err.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Duplicate Strategy */}
              {validationResult.duplicate_count > 0 && (
                <div className="mb-6">
                  <Label className="mb-2 block">How should we handle duplicates?</Label>
                  <div className="flex space-x-3">
                    {[
                      { id: 'skip', label: 'Skip & Log', desc: 'Skip duplicate records' },
                      { id: 'update', label: 'Update Existing', desc: 'Update existing records' },
                      { id: 'abort', label: 'Abort Import', desc: 'Cancel if duplicates found' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setDuplicateStrategy(opt.id)}
                        className={`flex-1 p-3 rounded-lg border-2 text-left transition-colors ${
                          duplicateStrategy === opt.id
                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                            : 'border-slate-200 dark:border-slate-600'
                        }`}
                      >
                        <p className="font-medium text-slate-900 dark:text-white text-sm">{opt.label}</p>
                        <p className="text-xs text-slate-500">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setWizardStep(2)}>
                  Back
                </Button>
                <Button 
                  onClick={handleExecuteImport} 
                  disabled={importing || !validationResult.is_valid}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="execute-import-btn"
                >
                  {importing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Execute Import
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Complete */}
          {wizardStep === 4 && importResult && (
            <div className="text-center py-8">
              {importResult.success ? (
                <>
                  <div className="h-16 w-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mx-auto mb-4 flex items-center justify-center">
                    <Check className="h-8 w-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Import Complete!</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-6">
                    Successfully imported {importResult.stats.success} records
                  </p>
                </>
              ) : (
                <>
                  <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 mx-auto mb-4 flex items-center justify-center">
                    <X className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Import Failed</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-6">
                    Please check the error log and try again
                  </p>
                </>
              )}
              
              <div className="grid grid-cols-4 gap-4 mb-6 max-w-md mx-auto">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{importResult.stats.total}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{importResult.stats.success}</p>
                  <p className="text-xs text-slate-500">Success</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{importResult.stats.skipped}</p>
                  <p className="text-xs text-slate-500">Skipped</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{importResult.stats.failed}</p>
                  <p className="text-xs text-slate-500">Failed</p>
                </div>
              </div>
              
              <div className="flex justify-center space-x-4">
                <Button variant="outline" onClick={resetWizard}>
                  Import More Data
                </Button>
                {uploadResult?.migration_id && (
                  <Button variant="outline" onClick={() => downloadReport(uploadResult.migration_id)}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Report
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Migration History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Migration History</DialogTitle>
            <DialogDescription>
              View and manage past data migrations
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {migrations.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No migrations yet</p>
            ) : (
              migrations.map((migration) => (
                <div
                  key={migration.migration_id}
                  className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(migration.status)}`}>
                        {migration.status}
                      </span>
                      <span className="font-medium text-slate-900 dark:text-white capitalize">
                        {migration.migration_type}
                      </span>
                      <span className="text-slate-500 text-sm">
                        from {migration.source_system}
                      </span>
                    </div>
                    <span className="text-slate-400 text-xs">
                      {new Date(migration.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-slate-500">
                      {migration.original_filename} • {migration.row_count} rows
                      {migration.import_stats && (
                        <span className="ml-2">
                          • {migration.import_stats.success} imported
                        </span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadReport(migration.migration_id)}
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                      {migration.status === 'completed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRollback(migration.migration_id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <RotateCcw className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
